/**
 * `<SupportAgentDialog>` — create or edit a human support-agent operational
 * profile (`AgentProfile`). Two modes:
 *   - `add`: pick a workspace member (who has no profile yet) + set initial
 *     availability / concurrency / skills → `POST /api/agent-profiles`.
 *   - `edit`: adjust availability / concurrency / skills of an existing
 *     profile → `PATCH /api/agent-profiles/:id`.
 *
 * The profile is operational state only; the `support_agent` / `support_lead`
 * ROLE (which grants access) is assigned separately on the Members page.
 */

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
  toast,
} from "@cometchat-ai/ui-dashboard";
import type { AuthClientError, PublicMember } from "@cometchat-ai/auth/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "./icons";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { FINAL_FONT } from "../layouts/final-polish";
import { formatApiError } from "../lib/format-error";
import {
  type AgentProfile,
  type Availability,
  type WorkingHours,
  agentProfileKeys,
  createAgentProfile,
  updateAgentProfile,
} from "../handoff/api";

const WEEKDAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
] as const;
type DayKey = (typeof WEEKDAYS)[number]["key"];

/** 30-minute steps as "HH:MM" (matches the backend regex); displayed without
 *  the leading zero ("9:00") to read like the Business-hours picker. */
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  return `${h}:${i % 2 ? "30" : "00"}`;
});
const timeLabel = (hm: string) => hm.replace(/^0/, "");

/** Per-day editor row: `on` gates whether the day is applied; `start`/`end`
 *  persist even while off so the (grayed) dropdowns stay populated. */
type DayRow = { on: boolean; start: string; end: string };

function emptyDayRows(): Record<DayKey, DayRow> {
  return Object.fromEntries(
    WEEKDAYS.map(({ key }) => [key, { on: false, start: "09:00", end: "18:00" }]),
  ) as Record<DayKey, DayRow>;
}

/** Seed editor rows from a stored schedule: a day present in `wh` is ON with
 *  its interval; every other day is OFF but keeps the default times. */
function toDayRows(wh: WorkingHours | null): Record<DayKey, DayRow> {
  const rows = emptyDayRows();
  if (!wh) return rows;
  for (const { key } of WEEKDAYS) {
    const iv = wh[key]?.[0];
    if (iv) rows[key] = { on: true, start: iv.start, end: iv.end };
  }
  return rows;
}

/** Curated common IANA zones + the viewer's own — a full list is thousands. */
function timezoneOptions(): string[] {
  const common = [
    "Asia/Kolkata",
    "America/New_York",
    "America/Chicago",
    "America/Los_Angeles",
    "America/Sao_Paulo",
    "Europe/London",
    "Europe/Berlin",
    "Africa/Johannesburg",
    "Asia/Dubai",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Australia/Sydney",
    "UTC",
  ];
  let detected = "UTC";
  try {
    detected = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    /* keep UTC */
  }
  return [...new Set([detected, ...common])];
}

/** One opens-at / closes-at dropdown for the working-hours editor. */
function TimeSelect({
  value,
  onChange,
  invalid,
  disabled,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        data-testid={testId}
        className={cn(
          "h-8 w-24 rounded-md text-[13px] tabular-nums",
          invalid && "border-[color:var(--color-danger)]",
        )}
      >
        <SelectValue>{timeLabel(value)}</SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {TIME_OPTIONS.map((tm) => (
          <SelectItem key={tm} value={tm} className="text-[13px] tabular-nums">
            {timeLabel(tm)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const AVAILABILITIES: readonly Availability[] = ["available", "unavailable"];

const formSchema = z.object({
  userId: z.string(),
  displayName: z.string().trim().max(120).optional(),
  availability: z.enum(["available", "unavailable"]),
  skills: z.string(),
});
type FormValues = z.input<typeof formSchema>;
type SubmitValues = z.output<typeof formSchema>;

function skillsToText(skills: string[]): string {
  return skills.join(", ");
}
function textToSkills(text: string): string[] {
  return text
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export interface SupportAgentDialogProps {
  workspaceSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit mode when set; add mode otherwise. */
  profile?: AgentProfile | null;
  /** Add mode: members eligible for a new profile (no existing profile yet). */
  assignableMembers?: ReadonlyArray<PublicMember>;
}

export function SupportAgentDialog({
  workspaceSlug,
  open,
  onOpenChange,
  profile = null,
  assignableMembers = [],
}: SupportAgentDialogProps) {
  const { t } = useTranslation("workspace");
  const queryClient = useQueryClient();
  const isEdit = profile !== null;

  const defaults: FormValues = {
    userId: profile?.userId ?? assignableMembers[0]?.userId ?? "",
    displayName: profile?.displayName ?? "",
    availability: profile?.availability ?? "unavailable",
    skills: profile ? skillsToText(profile.skills) : "",
  };

  const form = useForm<FormValues, unknown, SubmitValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults,
  });

  // Working hours live outside RHF (nested weekly structure). `scheduleOn`
  // off = no schedule = always in-shift (null hours). Each day carries its own
  // start/end even while OFF, so the dropdowns stay populated (grayed) and a
  // day's times survive toggling — only ON days are applied at save. Seeded on
  // open.
  const [scheduleOn, setScheduleOn] = useState(false);
  const [timezone, setTimezone] = useState<string>(() => timezoneOptions()[0] ?? "UTC");
  const [dayRows, setDayRows] = useState<Record<DayKey, DayRow>>(() => emptyDayRows());

  // Re-seed the form when the dialog opens or the edit target changes. `open`
  // + the target's id are the only real triggers; `defaults`/`form` are stable
  // enough here (re-reading them mid-open would clobber user input).
  useEffect(() => {
    if (!open) return;
    form.reset(defaults);
    const wh = profile?.workingHours ?? null;
    setScheduleOn(wh !== null);
    setDayRows(toDayRows(wh));
    setTimezone(profile?.timezone ?? timezoneOptions()[0] ?? "UTC");
  }, [open, profile?.id]);

  const toggleDay = (day: DayKey, on: boolean) =>
    setDayRows((prev) => ({ ...prev, [day]: { ...prev[day], on } }));
  const setDayTime = (day: DayKey, edge: "start" | "end", value: string) =>
    setDayRows((prev) => ({ ...prev, [day]: { ...prev[day], [edge]: value } }));

  // Only ON days become the applied schedule.
  const hours: WorkingHours = Object.fromEntries(
    WEEKDAYS.filter(({ key }) => dayRows[key].on).map(({ key }) => [
      key,
      [{ start: dayRows[key].start, end: dayRows[key].end }],
    ]),
  );

  const mutation = useMutation({
    mutationFn: (values: SubmitValues) =>
      isEdit
        ? updateAgentProfile(profile.id, {
            displayName: values.displayName?.trim() || null,
            availability: values.availability,
            skills: textToSkills(values.skills),
            workingHours: scheduleOn ? hours : null,
            timezone: scheduleOn ? timezone : null,
          })
        : createAgentProfile({
            userId: values.userId,
            displayName: values.displayName?.trim() || null,
            availability: values.availability,
            skills: textToSkills(values.skills),
            workingHours: scheduleOn ? hours : null,
            timezone: scheduleOn ? timezone : null,
          }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentProfileKeys.list(workspaceSlug) });
      toast.success(
        isEdit ? t("supportAgents.dialog.savedToast") : t("supportAgents.dialog.addedToast"),
      );
      onOpenChange(false);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("supportAgents.dialog.failed"), { description: formatApiError(err) });
    },
  });

  const noAssignable = !isEdit && assignableMembers.length === 0;
  // Block save if any enabled day has a non-positive interval (end <= start);
  // the backend rejects it too, this just gives immediate feedback.
  const hoursInvalid =
    scheduleOn &&
    WEEKDAYS.some(({ key }) => dayRows[key].on && dayRows[key].end <= dayRows[key].start);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="support-agent-dialog"
        style={{ fontFamily: FINAL_FONT }}
        className="flex max-h-[85vh] max-w-md flex-col gap-0 rounded-2xl p-0 sm:rounded-2xl"
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
            data-testid="support-agent-form"
            className="flex min-h-0 flex-1 flex-col"
            noValidate
          >
            <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
              <DialogTitle className="text-base font-semibold tracking-[0.01em]">
                {isEdit ? t("supportAgents.dialog.editTitle") : t("supportAgents.dialog.addTitle")}
              </DialogTitle>
              <DialogDescription className="text-[13px] text-muted-foreground">
                {isEdit
                  ? t("supportAgents.dialog.editSubtitle")
                  : t("supportAgents.dialog.addSubtitle")}
              </DialogDescription>
            </DialogHeader>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-2">
              {isEdit ? (
                <div className="space-y-1">
                  <span className="text-[13px] font-medium text-foreground">
                    {t("supportAgents.dialog.member")}
                  </span>
                  <div className="truncate text-[13px] text-muted-foreground">
                    {profile.displayName ? `${profile.displayName} · ` : ""}
                    {profile.email}
                  </div>
                </div>
              ) : (
                <FormField
                  control={form.control}
                  name="userId"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-[13px] font-medium text-foreground">
                        {t("supportAgents.dialog.member")}
                      </FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={noAssignable}
                      >
                        <FormControl>
                          <SelectTrigger
                            data-testid="support-agent-member"
                            className="h-9 rounded-lg text-[13px]"
                          >
                            <SelectValue
                              placeholder={t("supportAgents.dialog.memberPlaceholder")}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {assignableMembers.map((m) => {
                            const name = `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim();
                            return (
                              <SelectItem key={m.userId} value={m.userId}>
                                {name ? `${name} · ${m.email}` : m.email}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormDescription className="text-xs text-muted-foreground">
                        {noAssignable
                          ? t("supportAgents.dialog.noAssignable")
                          : t("supportAgents.dialog.memberHint")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-[13px] font-medium text-foreground">
                      {t("supportAgents.dialog.name")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("supportAgents.dialog.namePlaceholder")}
                        data-testid="support-agent-name"
                        className="h-9 rounded-lg text-[13px] md:text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                        style={{ fontFamily: "inherit" }}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-xs text-muted-foreground">
                      {t("supportAgents.dialog.nameHint")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="availability"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-[13px] font-medium text-foreground">
                      {t("supportAgents.dialog.availability")}
                    </FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger
                          data-testid="support-agent-availability"
                          className="h-9 rounded-lg text-[13px]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {AVAILABILITIES.map((a) => (
                          <SelectItem key={a} value={a}>
                            {t(`supportAgents.availability.${a}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="skills"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel className="text-[13px] font-medium text-foreground">
                      {t("supportAgents.dialog.skills")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("supportAgents.dialog.skillsPlaceholder")}
                        data-testid="support-agent-skills"
                        className="h-9 rounded-lg text-[13px] md:text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                        style={{ fontFamily: "inherit" }}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-xs text-muted-foreground">
                      {t("supportAgents.dialog.skillsHint")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-[13px] font-medium text-foreground">
                      {t("supportAgents.dialog.workingHours")}
                    </Label>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t("supportAgents.dialog.workingHoursHint")}
                    </p>
                  </div>
                  <Switch
                    checked={scheduleOn}
                    onCheckedChange={setScheduleOn}
                    data-testid="support-agent-hours-toggle"
                    aria-label={t("supportAgents.dialog.workingHours")}
                  />
                </div>

                {scheduleOn ? (
                  <div className="rounded-lg border border-border">
                    <div className="space-y-1.5 border-b border-border p-3">
                      <Label className="text-xs text-muted-foreground">
                        {t("supportAgents.dialog.timezone")}
                      </Label>
                      <Select value={timezone} onValueChange={setTimezone}>
                        <SelectTrigger
                          className="h-9 rounded-lg text-[13px]"
                          data-testid="support-agent-timezone"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {timezoneOptions().map((tz) => (
                            <SelectItem key={tz} value={tz} className="text-[13px]">
                              {tz}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Business-hours-style rows: fixed height so toggling a day
                        never shifts the layout (dropdowns ⇄ "Closed"). */}
                    <div className="divide-y divide-border">
                      {WEEKDAYS.map(({ key, label }) => {
                        const { on, start, end } = dayRows[key];
                        const invalid = on && end <= start;
                        return (
                          <div key={key} className="flex h-12 items-center gap-3 px-3">
                            <span className="w-24 shrink-0 text-[13px] font-medium text-foreground">
                              {label}
                            </span>
                            <div className="ml-auto flex h-8 items-center justify-end gap-2">
                              {on ? (
                                <>
                                  <TimeSelect
                                    value={start}
                                    onChange={(v) => setDayTime(key, "start", v)}
                                    testId={`support-agent-${key}-start`}
                                  />
                                  <span className="text-[13px] text-muted-foreground">–</span>
                                  <TimeSelect
                                    value={end}
                                    onChange={(v) => setDayTime(key, "end", v)}
                                    invalid={invalid}
                                    testId={`support-agent-${key}-end`}
                                  />
                                </>
                              ) : (
                                <span className="text-[13px] text-muted-foreground">
                                  {t("supportAgents.dialog.dayOff")}
                                </span>
                              )}
                            </div>
                            <Switch
                              checked={on}
                              onCheckedChange={(v) => toggleDay(key, v)}
                              aria-label={label}
                              data-testid={`support-agent-day-${key}`}
                              className="shrink-0"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <DialogFooter className="flex-row gap-2 px-5 pb-5 pt-4">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
                data-testid="support-agent-cancel"
                className="h-8 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-secondary-hover"
              >
                <X className="h-4 w-4" aria-hidden="true" />
                {t("supportAgents.dialog.cancel")}
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={mutation.isPending || noAssignable || hoursInvalid}
                data-testid="support-agent-submit"
                className="h-8 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
              >
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                {isEdit ? t("supportAgents.dialog.save") : t("supportAgents.dialog.add")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
